import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, Button, Stat } from "@/components/UI";
import { ChefHat, ForkKnife, Book, FileXls, Printer, ListNumbers } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { today, fmt } from "./rations/constants";
import { PrintableFeedList } from "./rations/PrintableFeedList";
import { RationsTab } from "./rations/RationsTab";
import { EventsTab } from "./rations/EventsTab";
import { CatalogTab } from "./rations/CatalogTab";
import { FeedOrderTab } from "./rations/FeedOrderTab";

const TABS = [
  { id: "rations", label: "Rasyonlar", icon: ChefHat },
  { id: "events", label: "Yemleme Geçmişi", icon: ForkKnife },
  { id: "catalog", label: "Yem Kataloğu", icon: Book },
  { id: "feed-order", label: "Yapım Sırası", icon: ListNumbers },
];

export default function Rations() {
  const { user } = useAuth();
  const canSeeCosts = !!user?.can_see_costs;
  const [tab, setTab] = useState("rations");
  const [rations, setRations] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [events, setEvents] = useState([]);
  const [feedOrder, setFeedOrder] = useState([]);

  const load = () => {
    api.get("/rations").then(r => setRations(r.data)).catch(() => {});
    api.get("/stock").then(r => setStocks(r.data)).catch(() => {});
    api.get("/feed-catalog").then(r => setCatalog(r.data)).catch(() => {});
    api.get("/rations/events?limit=200").then(r => setEvents(r.data)).catch(() => {});
    api.get("/feed-order").then(r => setFeedOrder(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const totalDailyCost = useMemo(() => rations.reduce((s, r) => s + Number(r.total_cost || 0), 0), [rations]);
  const totalDailyKg = useMemo(() => rations.reduce((s, r) => s + Number(r.total_kg || 0), 0), [rations]);
  const notFedToday = useMemo(() => rations.filter(r => !r.fed_today).length, [rations]);
  const totalAnimals = useMemo(() => rations.reduce((s, r) => s + Number(r.animal_count || 0), 0), [rations]);

  const downloadExcel = async () => {
    if (rations.length === 0) {
      toast.error("Önce en az bir rasyon oluşturun");
      return;
    }
    try {
      const res = await fetch(`${API}/rations/export`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("İndirilemedi");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yem_listesi_${today()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch (err) {
      toast.error(err?.message || "İndirme hatası");
    }
  };

  const printList = () => {
    if (rations.length === 0) {
      toast.error("Önce en az bir rasyon oluşturun");
      return;
    }
    window.print();
  };

  return (
    <div data-testid="rations-page">
      <PageHeader
        title="Rasyon / Yem Yönetimi"
        subtitle="Sürünüzün günlük yem reçetelerini tanımlayın, sabah tek tıkla yemlemeyi kaydedin, stok otomatik düşsün."
        actions={
          <>
            <Button data-testid="rations-print-btn" onClick={printList}
                    className="!bg-white !text-[#1A1D1A] border border-[#E5E2DC]">
              <Printer size={14} className="inline mr-1" weight="duotone" /> Yazdır
            </Button>
            <Button data-testid="rations-excel-btn" onClick={downloadExcel}
                    className="!bg-[#2D4A36]">
              <FileXls size={14} className="inline mr-1" weight="duotone" /> Excel İndir
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat testId="rations-stat-count" label="Rasyon Sayısı" value={rations.length} tone="accent" />
        <Stat testId="rations-stat-animals" label="Toplam Hayvan" value={totalAnimals} unit="baş" tone="ok" />
        <Stat testId="rations-stat-kg" label="Günlük Yem" value={fmt(totalDailyKg, 0)} unit="kg" tone="warn" />
        {canSeeCosts && <Stat testId="rations-stat-cost" label="Günlük Maliyet" value={fmt(totalDailyCost, 0)} unit="₺" tone={totalDailyCost > 0 ? "danger" : "muted"} />}
      </div>

      {notFedToday > 0 && (
        <div className="mb-6 p-3 border border-[#D6A64A] bg-[#FBF3E0] rounded flex items-center justify-between" data-testid="rations-reminder">
          <div className="text-sm text-[#5e4a1a]">
            <b>⚠️ Bugün {notFedToday} rasyonun yemlemesi henüz kaydedilmedi</b> — Rasyonlar sekmesinden "Bugün Yemle" butonuna basın.
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1 mb-6 border-b border-[#E5E2DC]">
        {TABS.map(t => (
          <button key={t.id} data-testid={`rations-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === t.id ? "border-[#1A1D1A] text-[#1A1D1A]" : "border-transparent text-[#6b746c] hover:text-[#1A1D1A]"}`}>
            <t.icon size={16} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "rations" && <RationsTab rations={rations} stocks={stocks} catalog={catalog} feedOrder={feedOrder} reload={load} />}
      {tab === "events" && <EventsTab events={events} rations={rations} reload={load} />}
      {tab === "catalog" && <CatalogTab catalog={catalog} reload={load} />}
      {tab === "feed-order" && <FeedOrderTab feedOrder={feedOrder} reload={load} />}

      <PrintableFeedList rations={rations} />
    </div>
  );
}

// ---------------- PRINTABLE VIEW (only visible when printing) ----------------
